'use client'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form'

// React-hook-form + zod imports
import { useForm } from 'react-hook-form'
import { signInSchema, TSignInSchema } from '@/schemas/auth'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../ui/card'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { useState, useCallback } from 'react'
import { LockIcon } from 'lucide-react'
import { SyncLoader } from 'react-spinners'
import { signIn } from '@/server/auth/actions'
import { purgeClientCaches } from '@/lib/auth-purge'
import { useRouter, Link } from '@/i18n/routing'
import { toast } from 'sonner'
import { setCurrentUser } from '@/store/currentUserSlice'
import { useAppDispatch } from '@/store'
import { useTranslations } from 'next-intl'
import { FireTransition } from './fire-transition'

const SignInForm = () => {
  const router = useRouter()
  const t = useTranslations('SignIn')
  const dispatch = useAppDispatch()

  const [isPending, setIsPending] = useState(false)
  const [showFire, setShowFire] = useState(false)
  const signInForm = useForm<TSignInSchema>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      username: '',
      password: '',
      rememberMe: false
    }
  })

  const onSubmit = async (values: TSignInSchema) => {
    setIsPending(true)

    try {
      const authenticated = await signIn(values)

      if (authenticated.error) {
        toast.error(t('error'))
        setIsPending(false)
        return
      }

      if (authenticated.success) {
        await purgeClientCaches()
        const response = await fetch('/api/auth/current-user')

        if (!response.ok) {
          setIsPending(false)
          return toast.error(t('error'))
        }

        const data = await response.json()
        const user = data.data

        dispatch(setCurrentUser(user))

        toast.success(t('success'))
        window.dispatchEvent(new CustomEvent('auth-fire-start'))
        setShowFire(true)
      }
    } catch (error) {
      console.error(error)
      toast.error(t('error'))
    } finally {
      setIsPending(false)
    }
  }

  const handleFireComplete = useCallback(() => {
    router.replace('/dashboards')
  }, [router])

  return (
    <>
    <FireTransition active={showFire} onComplete={handleFireComplete} />
    <Card className='w-96' style={{ '--card': '0 0% 100%', '--card-foreground': '240 10% 3.9%', '--muted-foreground': '240 3.8% 46.1%', '--input': '240 5.9% 90%', '--border': '240 5.9% 90%', '--ring': '240 5.9% 10%', '--primary': '0 84% 60%', '--primary-foreground': '0 0% 100%' } as React.CSSProperties}>
      <CardHeader>
        <CardTitle>{t('signIn')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...signInForm}>
          <form
            onSubmit={signInForm.handleSubmit(onSubmit)}
            className='flex flex-col gap-4'
          >
            <FormField
              control={signInForm.control}
              name='username'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('username')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('usernamePH')}
                      disabled={isPending}
                      type='text'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={signInForm.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('password')}</FormLabel>
                  <FormControl className=''>
                    <Input
                      placeholder='••••••'
                      disabled={isPending}
                      type='password'
                      {...field}
                      className='relative'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type='submit'
              className='w-full flex gap-2 !bg-[#F04438] hover:!bg-[#D83A30] !text-white'
              disabled={isPending}
            >
              {isPending ? (
                <SyncLoader size={5} speedMultiplier={0.5} />
              ) : (
                <>
                  <LockIcon className='w-4 h-4' />
                  {t('signIn')}
                </>
              )}
            </Button>
            <p className='text-sm text-muted-foreground text-center'>
              {t('forgotPasswordIntro')}{' '}
              <Link
                href='/forgot-password'
                className='font-medium text-[#F04438] hover:text-[#D83A30] hover:underline'
              >
                {t('forgotPassword')}
              </Link>
            </p>
          </form>
        </Form>
      </CardContent>
    </Card>
    </>
  )
}

export default SignInForm
